import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { EmployeePicker } from '@/components/registration/EmployeePicker';
import { mfgApi } from './mfgApi';
import { todayJst } from '@/utils/jstDate';

const today = () => todayJst();

export default function ScheduleFormDialog({ open, schedule, equipmentId, equipmentList, onClose, onSaved }) {
  const { t } = useT();
  const [eqId, setEqId] = useState('');
  const [title, setTitle] = useState('');
  const [intervalType, setIntervalType] = useState('days');
  const [intervalDays, setIntervalDays] = useState(30);
  const [fixedDates, setFixedDates] = useState('');
  const [checklist, setChecklist] = useState('');
  const [assignee, setAssignee] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [lastDone, setLastDone] = useState('');
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // 2026-10-22 · 担当者を選ぶための社員名簿（在籍のみ）
  useEffect(() => {
    if (!open) return;
    mfgApi.employees()
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.items || []);
        setEmployees(rows.filter((e) => (e.status || 'active') === 'active')
          .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
      })
      .catch(() => setEmployees([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setEqId(schedule.equipment_id || equipmentId || '');
      setTitle(schedule.title || '');
      setIntervalType(schedule.interval_type || 'days');
      setIntervalDays(schedule.interval_days ?? 30);
      setFixedDates((schedule.fixed_dates || []).join(', '));
      setChecklist((schedule.checklist || []).join('\n'));
      setAssignee(schedule.assignee || '');
      setAssigneeId(schedule.assignee_employee_id || '');
      setLastDone(schedule.last_done_on || '');
      setActive(schedule.active !== false);
      setNotes(schedule.notes || '');
    } else {
      setEqId(equipmentId || ''); setTitle(''); setIntervalType('days'); setIntervalDays(30);
      setFixedDates(''); setChecklist(''); setAssignee(''); setLastDone(''); setActive(true); setNotes('');
    }
  }, [open, schedule, equipmentId]);

  const save = async () => {
    if (!eqId) { toast.error(t('mfg.maint.equipment')); return; }
    if (!title.trim()) { toast.error(t('mfg.maint.scheduleTitle')); return; }
    setBusy(true);
    try {
      const body = {
        equipment_id: eqId, title: title.trim(), interval_type: intervalType,
        interval_days: intervalType === 'days' ? Number(intervalDays) || 0 : null,
        fixed_dates: intervalType === 'date' ? fixedDates.split(',').map((s) => s.trim()).filter(Boolean) : [],
        checklist: checklist.split('\n').map((s) => s.trim()).filter(Boolean),
        assignee: assignee || null,
        assignee_employee_id: assigneeId || null,
        last_done_on: lastDone || null, active, notes: notes || null,
      };
      const res = schedule ? await mfgApi.updateSchedule(schedule.id, body) : await mfgApi.createSchedule(body);
      toast.success(t('mfg.common.saved')); onSaved?.(res);
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="schedule-form-dialog">
        <DialogHeader><DialogTitle>{schedule ? t('mfg.maint.editSchedule') : t('mfg.maint.newSchedule')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!equipmentId && (
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.equipment')}</Label>
              <Select value={eqId} onValueChange={setEqId}>
                <SelectTrigger data-testid="schedule-equipment-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.maint.equipment')} /></SelectTrigger>
                <SelectContent>{(equipmentList || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.asset_no} — {e.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.scheduleTitle')}</Label>
            <Input data-testid="schedule-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.intervalType')}</Label>
              <Select value={intervalType} onValueChange={setIntervalType}>
                <SelectTrigger data-testid="schedule-interval-type" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="days">{t('mfg.maint.intervalType.days')}</SelectItem><SelectItem value="date">{t('mfg.maint.intervalType.date')}</SelectItem></SelectContent>
              </Select></div>
            {intervalType === 'days'
              ? <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.intervalDays')}</Label><Input type="number" data-testid="schedule-interval-days" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} className="h-10 mt-1" /></div>
              : <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.lastDone')}</Label><DateField value={lastDone || ''} onChange={(v) => setLastDone(v)} className="mt-1" inputClassName="h-10" /></div>}
          </div>
          {intervalType === 'date'
            ? <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.fixedDates')}</Label><Input value={fixedDates} onChange={(e) => setFixedDates(e.target.value)} placeholder="2026-03-31, 2026-09-30" className="h-10 mt-1 font-mono text-sm" /></div>
            : <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.lastDone')}</Label><DateField value={lastDone || ''} onChange={(v) => setLastDone(v)} className="mt-1" inputClassName="h-10" /></div>}
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.checklist')}</Label>
            <Textarea data-testid="schedule-checklist" value={checklist} onChange={(e) => setChecklist(e.target.value)} rows={3} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.assignee')}</Label>
              {/* 2026-10-22 · 社員名簿から選ぶ（古い行の手入力はそのまま出す） */}
              <EmployeePicker value={assignee} employees={employees}
                onChange={(name) => {
                  setAssignee(name);
                  const hit = employees.find((e) => e.name === name);
                  setAssigneeId(hit ? hit.id : '');
                }}
                testId="schedule-assignee" />
            </div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={active} onCheckedChange={setActive} data-testid="schedule-active-switch" /><span className="text-sm text-zinc-600">{t('mfg.maint.active')}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="schedule-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.common.saving') : t('mfg.common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
